/**
 * Service Worker registration + SQLite-backed (or demo) delivery-point/redemption applications.
 */
import { AppConfig } from '@core/domain/AppConfig.ts';
import { withRetry } from '@core/domain/utils/asyncResilience.ts';
import { LocalSqliteRuntimeFactory } from '@src/modules/shared/bootstrap/infrastructure/localSqliteRuntime.ts';
import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import type { RedencionBonoApplicationService } from '@src/modules/redencion_bono/application/RedencionBonoApplicationService.ts';
import { bootstrapPuntosEntregaSql } from '@src/modules/puntos_entrega/infrastructure/factories/createPuntosEntregaApplication.ts';
import { createPuntosEntregaApplicationPlaceholder } from '@src/modules/puntos_entrega/infrastructure/factories/puntosEntregaApplicationPlaceholder.ts';
import { PuntosEntregaApplicationService } from '@src/modules/puntos_entrega/application/PuntosEntregaApplicationService.ts';
import { createRedencionBonoApplicationService } from '@src/modules/redencion_bono/infrastructure/factories/createRedencionBonoApplication.ts';
import { createRedencionBonoApplicationPlaceholder } from '@src/modules/redencion_bono/infrastructure/factories/redencionBonoApplicationPlaceholder.ts';
import { registerOfflineServiceWorkerFromEnv } from '@src/core/bootstrap/offlineServiceWorkerClient.ts';

export type FeatureShellApplications = {
  puntosEntregaShell: PuntosEntregaApplicationService;
  redencionBonoApplication: RedencionBonoApplicationService;
};

/** Safe placeholders when {@link AppContainer} runs without injected feature apps (e.g. narrow integration tests). */
export function createDefaultFeatureShellApplications(): FeatureShellApplications {
  return {
    puntosEntregaShell: createPuntosEntregaApplicationPlaceholder(),
    redencionBonoApplication: createRedencionBonoApplicationPlaceholder(),
  };
}

/** Bootstraps feature shell applications backed by the local SQLite runtime. */
export async function bootstrapSqliteBackedFeatureShellApplications(
  runtime: LocalSqliteRuntime,
): Promise<FeatureShellApplications> {
  return {
    puntosEntregaShell: await bootstrapPuntosEntregaSql(runtime),
    redencionBonoApplication: createRedencionBonoApplicationService(runtime),
  };
}

export type OfflineSqliteInfraHandles = {
  getRuntime(): LocalSqliteRuntime;
  getPuntosEntregaShell(): PuntosEntregaApplicationService;
  getRedencionBonoApplicationService(): RedencionBonoApplicationService;
  bootstrap(): Promise<void>;
};

/** SQLite + service worker bootstrap; call `bootstrap()` before reading use cases. */
export function createOfflineSqliteInfrastructure(): OfflineSqliteInfraHandles {
  const runtime = LocalSqliteRuntimeFactory.create();
  let applications: FeatureShellApplications | undefined;

  const requireApplications = (): FeatureShellApplications => {
    if (!applications) {
      throw new Error('Offline infra: call bootstrap() before accessing use cases.');
    }
    return applications;
  };

  return {
    getRuntime: () => runtime,
    getPuntosEntregaShell: () => requireApplications().puntosEntregaShell,
    getRedencionBonoApplicationService: () => requireApplications().redencionBonoApplication,
    async bootstrap(): Promise<void> {
      await registerOfflineServiceWorkerFromEnv(import.meta.env);
      if (!AppConfig.useLocalSqliteClient()) {
        applications = {
          puntosEntregaShell: await bootstrapPuntosEntregaSql(runtime),
          redencionBonoApplication: createRedencionBonoApplicationPlaceholder(),
        };
        return;
      }
      await withRetry(
        async () => {
          applications = await bootstrapSqliteBackedFeatureShellApplications(runtime);
        },
        {
          maxAttempts: 4,
          baseDelayMs: 500,
          maxDelayMs: 10_000,
          jitterRatio: 0.2,
          isRetryable: (err) => {
            const m = err instanceof Error ? err.message : String(err);
            if (/\(404\)|\(403\)/.test(m)) return false;
            if (/db:init-local/i.test(m)) return false;
            return true;
          },
        },
      );
    },
  };
}
